import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { X, ArrowRight } from "lucide-react";
import { DEFCON_COLORS } from "@/lib/defcon";
import StreamPlayer from "@/components/StreamPlayer";

const PKEY = "ff_defcon_popup_seen";

function fmtCountdown(ms) {
  if (ms == null || isNaN(ms) || ms <= 0) return "starting now";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s % 60}s`;
  return `${m}m ${s % 60}s`;
}

// Site-wide takeover popup shown ONCE per DEFCON-1 issuance: the meaning of
// DEFCON 1, a live countdown, and a Watch-Live button to the stream.
export default function DefconNuclearPopup() {
  const [d, setD] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/defcon");
        setD(data);
        if (data.current_level === 1) {
          const key = `1|${data.level_changed_at || ""}`;
          let seen = "";
          try { seen = sessionStorage.getItem(PKEY) || ""; } catch {}
          if (seen !== key) setOpen(true);
        }
      } catch {}
    })();
  }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!open || !d || d.current_level !== 1) return null;

  const color = DEFCON_COLORS[1];
  const meta = (d.levels && d.levels["1"]) || {};
  const countdown = d.next_air ? fmtCountdown(new Date(d.next_air).getTime() - now) : "";

  const close = () => {
    try { sessionStorage.setItem(PKEY, `1|${d.level_changed_at || ""}`); } catch {}
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="defcon-nuclear-popup">
      <div
        className="relative w-full max-w-2xl border p-8 defcon-flash"
        style={{ borderColor: color, background: `linear-gradient(160deg, ${color}22 0%, #0a0a0a 70%)`, boxShadow: `0 0 60px ${color}66` }}
      >
        <button onClick={close} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="defcon-popup-close" aria-label="Close">
          <X size={20} />
        </button>
        <div className="label-eyebrow mb-4" style={{ color }}>☢ {d.name || "Freshy Defcon"} · {d.show_title}</div>
        <div className="flex items-end gap-4 mb-4">
          <div className="font-display leading-none" style={{ fontSize: "5rem", color }}>1</div>
          <div className="pb-2">
            <div className="font-display text-3xl leading-none">{meta.label || "Go Nuclear"}</div>
          </div>
        </div>
        <p className="text-[var(--ff-text-dim)] text-sm leading-relaxed mb-6" data-testid="defcon-popup-meaning">
          {meta.description || d.meaning}
        </p>

        {d.next_air_label && (
          <div className="border border-[var(--ff-border)] bg-black/30 p-4 mb-6">
            <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ff-text-dim)] mb-1">Goes Live</div>
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <span className="font-display text-2xl">{d.next_air_label}</span>
              <span className="font-mono-ui text-sm" style={{ color }} data-testid="defcon-popup-countdown">{countdown}</span>
            </div>
          </div>
        )}

        {d.stream_url && <StreamPlayer url={d.stream_url} color={color} className="mb-5" />}

        <Link
          to="/defcon"
          onClick={close}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] border border-[var(--ff-border)] hover:border-white transition-colors"
          data-testid="defcon-popup-page-link"
        >
          Full Status <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
